<template>
  <home-form-table-toolbar>
    <home-form-table-refresh-tool
      :disabled="toolbarIsDisabled"
      @click="
        startAction(
          objects.edited,
          'refresh',
          emitEvent,
          'multiple-selection-refresh'
        )
      "
    />

    <home-form-table-select-all-tool
      :disabled="toolbarIsDisabled || objects.selected === objects.total"
      @click="emitEvent('multiple-selection-select-all')"
    />

    <home-form-table-select-invert-tool
      :disabled="toolbarIsDisabled"
      @click="emitEvent('multiple-selection-invert-selection')"
    />

    <home-form-table-select-none-tool
      :disabled="toolbarIsDisabled || objects.selected === 0"
      @click="emitEvent('multiple-selection-clear-selection')"
    />

    <home-form-table-clone-tool
      v-if="objectIsCloneable && canModifyObjects"
      :disabled="
        toolbarIsDisabled ||
          !canUseTool('store') ||
          editTool.selected ||
          objects.selected === 0
      "
      @click="emitEvent('multiple-selection-clone')"
    />

    <home-form-table-delete-tool
      v-if="canModifyObjects"
      :disabled="
        toolbarIsDisabled ||
          !canUseTool('destroy') ||
          editTool.selected ||
          objects.selected === 0
      "
      @click="dialogs.deleteObjects = true"
    />

    <home-form-table-toggle-edition-mode-tool
      v-if="canModifyObjects"
      :disabled="toolbarIsDisabled || !canUseTool('update')"
      :selected="editTool.selected"
      @click="
        startAction(
          objects.edited,
          'leaveEditionMode',
          changeEditionMode,
          !editTool.selected
        )
      "
    />

    <home-form-table-edit-selected-tool
      v-if="canModifyObjects"
      :disabled="
        toolbarIsDisabled || !editTool.selected || objects.selected === 0
      "
      :selected="editSelectedTool.selected"
      @click="toggleEditSelected"
    />

    <home-form-table-save-tool
      v-if="canModifyObjects"
      :disabled="toolbarIsDisabled || !(editTool.selected && objects.edited)"
      @click="save"
    />

    <home-form-table-export-to-csv-tool
      :disabled="toolbarIsDisabled || objects.selected === 0"
      :data-to-export="dataToExport"
      :file-name="exportationTitle"
      @click="emitEvent('multiple-selection-get-data-to-export')"
      @reset-exportation-data="dataToExport = null"
    />

    <span class="number-objects" v-if="objects.total">
      {{ $tc("Object", 2) + ": " + objects.total }}
    </span>

    <app-dialog-box
      :open="dialogs.leaveEditionMode"
      :title="$t('LeaveEditionMode')"
      :content="leaveEditionModeDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="dialogs.leaveEditionMode = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="
        executeAction('leaveEditionMode', changeEditionMode, false)
      "
    />

    <app-dialog-box
      :open="dialogs.refresh"
      :title="$t('RefreshData')"
      :content="refreshDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="dialogs.refresh = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="
        executeAction('refresh', emitEvent, 'multiple-selection-refresh')
      "
    />

    <app-dialog-box
      :open="dialogs.deleteObjects"
      :title="$t('DeleteObjects')"
      :content="deleteDialogMessage()"
      :main-action-label="$t('No')"
      @mainAction="dialogs.deleteObjects = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="
        executeAction('deleteObjects', emitEvent, 'multiple-selection-delete')
      "
    />

    <app-dialog-box
      :open="closureDelete.dialogOpen"
      :title="$t('DeleteObjects')"
      :content="deleteClosuresDialogMessage()"
      :main-action-label="
        numClosuresAssociatedIsLessSelected ? $t('No') : $t('Cancel')
      "
      @mainAction="$emit('closeDialogDeleteClosure')"
      @secondaryAction="emitEvent('multiple-selection-delete-closure')"
      :secondary-action-label="$t('Yes')"
      :secondary-action-disable="numClosuresAssociatedIsLessSelected"
    />
  </home-form-table-toolbar>
</template>

<script>
import { bus } from "@/main.js";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import HomeFormTableToolbar from "@/components/Home/Forms/_Atoms/TableToolbar";
import HomeFormTableRefreshTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Refresh";
import HomeFormTableSelectAllTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectAll";
import HomeFormTableSelectInvertTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectInvert";
import HomeFormTableSelectNoneTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/SelectNone";
import HomeFormTableCloneTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Clone";
import HomeFormTableDeleteTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Delete";
import HomeFormTableToggleEditionModeTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/ToggleEditionMode";
import HomeFormTableEditSelectedTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/EditSelected";
import HomeFormTableSelectSaveTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Save";
import HomeFormTableSaveTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/Save";
import HomeFormTableExportToCsvTool from "@/components/Home/Forms/_Atoms/TableToolbarButtons/ExportToCSV";
import AppDialogBox from "../../../_Molecules/AppDialogBox";

export default {
  name: "MultiSelectionToolbar",

  components: {
    AppDialogBox,
    HomeFormTableExportToCsvTool,
    HomeFormTableSaveTool,
    HomeFormTableSelectSaveTool,
    HomeFormTableEditSelectedTool,
    HomeFormTableToggleEditionModeTool,
    HomeFormTableDeleteTool,
    HomeFormTableCloneTool,
    HomeFormTableSelectNoneTool,
    HomeFormTableSelectInvertTool,
    HomeFormTableSelectAllTool,
    HomeFormTableRefreshTool,
    HomeFormTableToolbar
  },

  mixins: [checkPermissionsMixin],

  props: { 
    layer: {
      type: String,
      required: true
    },

    canModifyObjects: Boolean,

    canGenerateRequest: Boolean,

    closureDelete: Object
  },

  data() {
    return {
      toolbarIsDisabled: true,

      editTool: {
        selected: false
      },

      editSelectedTool: {
        selected: false
      },

      dialogs: {
        refresh: false,
        leaveEditionMode: false,
        deleteObjects: false
      },

      objects: {
        total: undefined,
        selected: 0,
        edited: false
      },

      dataToExport: null
    };
  },

  computed: {
    permissionName() {
      return this.getPermissionNameByLayer(this.layer);
    },

    /**
     * Checks if the current object is editable or not.
     * @returns {boolean}
     */
    objectIsCloneable() {
      return (
        this.layer.includes("pole") ||
        this.layer.includes("manhole") ||
        this.layer.includes("cabinet") ||
        this.layer.includes("building") ||
        this.layer.includes("pathway") ||
        this.layer.includes("fdp_zone")
      );
    },

    isTechnicalPoint() {
      return (
        this.layer.includes("pole") ||
        this.layer.includes("manhole") ||
        this.layer.includes("cabinet") ||
        this.layer.includes("toby")
      );
    },

    isTechnicalSite() {
      return (
        this.layer.includes("optical_line_termination") ||
        this.layer.includes("fiber_distribution_point")
      );
    },

    numClosuresAssociated() {
      if (this.closureDelete.associatedCables) {
        return Object.keys(this.closureDelete.associatedCables).length;
      } else {
        return null;
      }
    },

    numClosuresAssociatedIsLessSelected() {
      if (this.closureDelete.selected) {
        return this.numClosuresAssociated < this.closureDelete.selected.length;
      } else {
        return null;
      }
    },

    leaveEditionModeDialogMessage() {
      return (
        this.$i18n.t("UnsavedChanges") +
        "\n" +
        this.$i18n.t("CheckLeaveEditionMode") +
        "\n" +
        this.$i18n.t("ChangesWillBeLost")
      );
    },

    refreshDialogMessage() {
      return (
        this.$i18n.t("UnsavedChanges") +
        "\n" +
        this.$i18n.t("CheckRefreshData") +
        "\n" +
        this.$i18n.t("ChangesWillBeLost")
      );
    },

    exportationTitle() {
      const workspace = this.$store.getters.workspace.label;
      let layer = this.layer.replace("fc_", "");
      return workspace + "_" + layer;
    }
  },

  watch: {
    layer() {
      this.changeEditionMode(false);
    }
  },

  mounted() {
    /**
     * Change toolbar state
     */
    bus.$on("change-multi-selection-toolbar-state", enable => {
      this.toolbarIsDisabled = !enable;
    });

    /**
     * Opens a channel to receive updates on the total number of objects
     */
    bus.$on("multiple-selection-update-number-objects", numberObjects => {
      this.objects.total = numberObjects;
    });

    /**
     * Opens a channel to receive updates on the number of objects selected
     */
    bus.$on(
      "multiple-selection-update-number-selected-objects",
      numberSelectedObjects => {
        this.objects.selected = numberSelectedObjects;
      }
    );

    /**
     * Opens a channel to receive updates on the edition state of the objects
     */
    bus.$on("multiple-selection-update-edition-state", objectsAreEdited => {
      this.objects.edited = objectsAreEdited;
    });

    /**
     * Opens a channel to receive updates on the edition state of the objects
     */
    bus.$on("multiple-selection-deactivate-edit-selected", () => {
      this.editSelectedTool.selected = false;
      this.toolbarIsDisabled = false;
    });

    bus.$on("multiple-selection-set-data-to-export", dataToExport => {
      this.dataToExport = dataToExport;
    });
  },

  destroyed() {
    bus.$off("change-multi-selection-toolbar-state");
    bus.$off("multiple-selection-update-number-objects");
    bus.$off("multiple-selection-update-number-selected-objects");
    bus.$off("multiple-selection-update-edition-state");
    bus.$off("multiple-selection-deactivate-edit-selected");
    bus.$off("multiple-selection-set-data-to-export");
  },

  methods: {
    /**
     * Checks if the action can be immediately executed or not.
     * If not it opens a dialog box for the user to verify that he wants to go through with the action.
     * Else it executes the action immediately.
     * @param {Boolean} dialogCondition - condition to check if a verification is needed or not.
     * @param {String} dialog - name of the variable that controls the appearance of the dialog box.
     * @param action - action to be executed.
     * @param actionParameter - parameter to feed the action
     */
    startAction(dialogCondition, dialog, action, actionParameter) {
      if (dialogCondition) {
        this.dialogs[dialog] = true;
      } else {
        action(actionParameter);
      }
    },

    /**
     * Executes the given action and closes the respective dialog box.
     * @param {String} dialog - name of the variable that controls the appearance of the dialog box.
     * @param action - action to be executed.
     * @param actionParameter
     */
    executeAction(dialog, action, actionParameter = undefined) {
      if (actionParameter !== undefined) {
        action(actionParameter);
      } else {
        action();
      }
      this.dialogs[dialog] = false;
    },

    /**
     * Emits event (for the Multi Selection table) to initiate a certain action.
     * @param {String }eventName -  name of the event to be emitted.
     */
    emitEvent(eventName) {
      bus.$emit(eventName);
    },

    /**
     * Toggles the edition mode.
     */
    changeEditionMode(newValue) {
      this.editTool.selected = newValue;
      bus.$emit("multiple-selection-update-mode", this.editTool.selected);
    },

    toggleEditSelected() {
      if (!this.editSelectedTool.selected) {
        this.editSelectedTool.selected = true;
        this.toolbarIsDisabled = true;
        bus.$emit("multiple-selection-activate-edit-selected");
      } else {
        bus.$emit("startEditSelectedDeactivation", "fromToolbar");
      }
    },

    save() {
      this.emitEvent("multiple-selection-save");
      this.changeEditionMode(false);
    },

    canUseTool(action) {
      return this.userHasFibercloudPermission(this.permissionName, action);
    },

    /**
     * Composes the Delete dialog message according to the given object type.
     * @returns {string}
     */
    deleteDialogMessage() {
      let mainMessage = this.$i18n.t("CheckDeleteObjects");
      let secondaryMessage =
        "\n \n " + this.$i18n.t("RemoveAssociatedObjectWarning");
      if (
        this.isTechnicalSite ||
        this.isTechnicalPoint ||
        this.layer.includes("closure")
      ) {
        if (this.isTechnicalSite) {
          secondaryMessage += this.$i18n.tc("TechnicalLocal", 1);
        } else if (this.isTechnicalPoint) {
          secondaryMessage +=
            this.$i18n.tc("Closure", 1) +
            "\n " +
            this.$i18n.t("SelectedClosureHasCablesAssociated") +
            "\n \n " +
            this.$i18n.t("JoinPathwayMultiSelect");
        } else if (this.layer.includes("closure")) {
          secondaryMessage =
            "\n \n " + this.$i18n.t("SelectedClosureHasCablesAssociated");
        }
        return mainMessage + secondaryMessage + ".";
      } else return mainMessage;
    },

    deleteClosuresDialogMessage() {
      if (this.closureDelete.associatedCables) {
        if (this.numClosuresAssociatedIsLessSelected) {
          return (
            this.numClosuresAssociated +
            " " +
            this.$i18n.t("DeleteClosureWithoutAssociatedCables")
          );
        } else {
          return this.$i18n.t("OnlyClosureCablesAssociated");
        }
      } else {
        return "";
      }
    }
  }
};
</script>

<style scoped>
.number-objects {
  font-size: 10px;
  padding-top: 3px;
  width: 100%;
  text-align: end;
  padding-right: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/MultiSelectionForm/MultiSelectionToolbar.vue